import React, { useCallback, useState } from "react";
import "./styles/Login.css";
import { useAuth } from "../providers/AuthContext";
import { AuthService } from "../services/AuthService";
import { useNavigate } from "react-router-dom";
import { NavSections } from "../common/nav-sections";
import AuthForm from "./AuthForm";
import { Validator } from "../services/Validator";
import { toast } from "react-hot-toast";
import { APP_DOMAIN, APP_NAME, DarkOrLightType } from "../common/constants";
import SEO from "./SEO";
import { AppRoutes } from "../common/app-routes";
import { isSuccess } from "../common/apiResult";
import GoogleAuth from "./GoogleAuth";

interface LoginProps {
  theme: DarkOrLightType;
  changeActiveSection: (id: string) => void;
}

const Login: React.FC<LoginProps> = ({ changeActiveSection, theme }) => {
  const navigate = useNavigate();

  const { login: setUser } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleAuthError = useCallback((error: { message?: string } | undefined) => {
    const errorMessage = error?.message ?? "Something went wrong";
    toast.error(errorMessage)
    setError(errorMessage);
    setIsLoading(false);
  }, [setError, setIsLoading]);

  const handleLogin = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!Validator.validateLogin(email, password, setError)) {
        return;
      }
      setIsLoading(true);
      setError("");

      const response = await AuthService.login(email, password);

      if (!isSuccess(response)) {
        handleAuthError(response.error);

        return;
      }

      setUser(response.data.token);
      toast.success("Welcome back! 🎉");
      navigate(`/#${NavSections.WELCOME}`);
      changeActiveSection(NavSections.WELCOME);
      setIsLoading(false);
    },
    [email, password, setUser, navigate, setIsLoading, changeActiveSection, handleAuthError]
  );

  const onGoogleAuthenticationSuccess = useCallback(async (googleToken: string) => {
    const response = await AuthService.loginWithGoogle(googleToken);

    if (!isSuccess(response)) {
      handleAuthError(response.error);
      
      return;
    }

    setUser(response.data.token);

    toast.success("Welcome back! 🎉");

    navigate(`/#${NavSections.WELCOME}`);
    changeActiveSection(NavSections.WELCOME);
  }, [navigate, setUser, changeActiveSection, handleAuthError])

  return (
    <>
      <SEO
        title={`Login | ${APP_NAME}`}
        description="Access your Cutora account. Secure login with email or Google OAuth, and get back to building faster."
        url={`${APP_DOMAIN}${AppRoutes.LOGIN}`}
      />
      <div className="login-container">
        <h1 className="login-title">Login to {APP_NAME}</h1>
        <AuthForm
          email={email}
          password={password}
          error={error}
          onEmailChange={setEmail}
          onPasswordChange={setPassword}
          onSubmit={handleLogin}
          buttonText="Login"
          isLoading={isLoading}
        >
        </AuthForm>
        <div className="login-divider">or</div>
        <GoogleAuth theme={theme} onSuccess={async (credentials) => await onGoogleAuthenticationSuccess(credentials.credential ?? '')} onError={() => setError("Something went wrong with Google Authentication. Please try again!")} />
      </div>
    </>
  );
};

export default Login;